import { useCallback, useRef, useState } from 'react'
import { useAuth } from '../layout/AuthContext'

// Per-user, per-table column visibility, persisted in
// preferences.tableHidden[tableId] (a list of hidden column keys). `reset` also
// clears the stored widths (tableColumns[tableId]) and bumps `resetKey` so the
// table can remount and fall back to default widths.

export function useColumnVisibility(tableId: string) {
    const { preferences, savePreferences } = useAuth()

    const [hidden, setHidden] = useState<Set<string>>(() => new Set(preferences.tableHidden?.[tableId] ?? []))
    const [resetKey, setResetKey] = useState(0)

    const prefsRef = useRef(preferences)
    prefsRef.current = preferences

    const toggle = useCallback((key: string) => {
        const next = new Set(hidden)
        if (next.has(key)) next.delete(key)
        else next.add(key)
        setHidden(next)
        const all = { ...(prefsRef.current.tableHidden ?? {}), [tableId]: [...next] }
        savePreferences({ tableHidden: all })
    }, [hidden, tableId, savePreferences])

    const reset = useCallback(() => {
        setHidden(new Set())
        const cols = { ...(prefsRef.current.tableColumns ?? {}) }
        const hid = { ...(prefsRef.current.tableHidden ?? {}) }
        delete cols[tableId]
        delete hid[tableId]
        savePreferences({ tableColumns: cols, tableHidden: hid })
        setResetKey((k) => k + 1)
    }, [tableId, savePreferences])

    return { hidden, toggle, reset, resetKey }
}
